import type { MemberUser } from "@/lib/types";
import { STAMP_CARD_SLOT_COUNT, STAMP_REWARD_DISCOUNT_YEN } from "@/lib/stampConstants";
import {
  memberRankLabelEn,
  parseMemberRankKey,
  rankNamePlateStyle,
} from "@/lib/memberRank";

type Props = {
  user: MemberUser;
};

export default function StampCard({ user }: Props) {
  const { stamps, name } = user;
  const slotCount = STAMP_CARD_SLOT_COUNT;
  const rewardDiscountText = `¥${STAMP_REWARD_DISCOUNT_YEN.toLocaleString("ja-JP")}引き`;
  const filled = Math.min(Math.max(0, stamps), slotCount);
  const remainingStamps = Math.max(0, slotCount - filled);
  const rankKey = parseMemberRankKey(user.rank);
  const plate = rankNamePlateStyle(rankKey);

  return (
    <div
      className="mx-auto w-full max-w-sm rounded-2xl p-5"
      style={{
        background: "linear-gradient(135deg, #1a2f2f 0%, #0d1f1f 100%)",
        border: "1px solid rgba(212,175,55,0.3)",
      }}
    >
      <div
        className="mb-4 rounded-xl px-4 py-3 text-center"
        style={{
          background: plate.background,
          border: plate.border,
          boxShadow: plate.shadow,
        }}
      >
        <p
          className="text-sm font-bold tracking-wide truncate"
          style={{ color: plate.nameColor, fontFamily: "Noto Sans JP, sans-serif" }}
        >
          {name || "ゲスト"} 様
        </p>
        <p
          className="text-[10px] mt-1 font-bold tracking-widest uppercase"
          style={{ color: plate.accentColor, fontFamily: "Montserrat, sans-serif" }}
        >
          {memberRankLabelEn(rankKey)} member
        </p>
      </div>

      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold tracking-wider" style={{ color: "#d4af37", fontFamily: "Montserrat, sans-serif" }}>
          STAMP CARD
        </h3>
        <span className="text-xs" style={{ color: "rgba(244,229,195,0.6)" }}>
          <span style={{ color: "#d4af37", fontFamily: "Montserrat, sans-serif", fontWeight: "bold" }}>{filled}</span>
          <span style={{ color: "rgba(244,229,195,0.4)" }}> / {slotCount}</span>
        </span>
      </div>

      <div className="grid grid-cols-5 gap-1.5 mb-4">
        {Array.from({ length: slotCount }).map((_, i) => {
          const isStamped = i < filled;
          const isLast = i === slotCount - 1;

          return (
            <div
              key={i}
              className="relative flex items-center justify-center rounded-xl"
              style={{
                aspectRatio: "1",
                background: isStamped
                  ? "linear-gradient(135deg, #b8860b, #d4af37)"
                  : "rgba(212,175,55,0.08)",
                border: isStamped
                  ? "1px solid rgba(212,175,55,0.8)"
                  : "1px solid rgba(212,175,55,0.2)",
                boxShadow: isStamped ? "0 2px 8px rgba(212,175,55,0.3)" : "none",
              }}
            >
              {isLast ? (
                <div className="flex flex-col items-center justify-center w-full h-full">
                  <i
                    className="ri-vip-crown-fill text-base"
                    style={{ color: isStamped ? "#1a1a2e" : "rgba(212,175,55,0.3)" }}
                  ></i>
                  <span
                    className="text-xs mt-0.5 font-bold"
                    style={{
                      color: isStamped ? "#1a1a2e" : "rgba(212,175,55,0.3)",
                      fontSize: "8px",
                      fontFamily: "Montserrat, sans-serif",
                    }}
                  >
                    特典
                  </span>
                </div>
              ) : (
                <div className="flex items-center justify-center w-full h-full">
                  {isStamped ? (
                    <i className="ri-flower-fill text-lg" style={{ color: "#1a1a2e" }}></i>
                  ) : (
                    <i className="ri-flower-line text-lg" style={{ color: "rgba(212,175,55,0.25)" }}></i>
                  )}
                </div>
              )}

              <span
                className="absolute bottom-0.5 right-1 text-xs"
                style={{
                  fontSize: "8px",
                  color: isStamped ? "rgba(26,26,46,0.7)" : "rgba(212,175,55,0.2)",
                  fontFamily: "Montserrat, sans-serif",
                }}
              >
                {i + 1}
              </span>
            </div>
          );
        })}
      </div>

      <div className="mb-3">
        <div className="h-1.5 rounded-full overflow-hidden" style={{ background: "rgba(212,175,55,0.1)" }}>
          <div
            className="h-full rounded-full"
            style={{
              width: `${slotCount ? (filled / slotCount) * 100 : 0}%`,
              background: "linear-gradient(90deg, #b8860b, #d4af37, #f4e5c3)",
              transition: "width 1s ease",
            }}
          />
        </div>
      </div>

      <div
        className="flex items-center gap-2 p-3 rounded-xl"
        style={{ background: "rgba(212,175,55,0.08)", border: "1px solid rgba(212,175,55,0.15)" }}
      >
        <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
          <i className="ri-gift-line text-base" style={{ color: "#d4af37" }}></i>
        </div>
        <div>
          <p className="text-xs font-medium" style={{ color: "#f4e5c3" }}>
            {slotCount}スタンプ達成で会計から{rewardDiscountText}
          </p>
          <p className="text-xs mt-0.5" style={{ color: "rgba(244,229,195,0.5)" }}>
            {remainingStamps > 0
              ? `あと ${remainingStamps} スタンプで${rewardDiscountText}`
              : `カード達成！会計時に${rewardDiscountText}をご利用ください`}
          </p>
        </div>
      </div>
    </div>
  );
}
